/* eslint-disable no-console */
import { isPlainObject } from '@/utils';

import { analyticsConfig } from './config';
import TrackEvent from './types';

// Extend globalThis to include gtag
declare global {
  interface Window {
    gtag?: (
      command: 'event' | 'config' | 'js' | 'set',
      targetIdOrParams: string | Record<string, unknown>,
      config?: Record<string, unknown>
    ) => void;
  }
  var gtag:
    | ((
        command: 'event' | 'config' | 'js' | 'set',
        targetIdOrParams: string | Record<string, unknown>,
        config?: Record<string, unknown>
      ) => void)
    | undefined;
}

/**
 * User properties for GA4
 * @see https://developers.google.com/analytics/devguides/collection/ga4/user-properties
 */
export interface UserProperties {
  [key: string]: string | number | boolean | null;
}

/**
 * Exception parameters for error tracking
 * @see https://developers.google.com/analytics/devguides/collection/ga4/exceptions
 */
export interface ExceptionParams {
  description?: string;
  fatal?: boolean;
}

class AnalyticsService {
  private isInitialized = false;
  private queue: Array<() => void> = [];
  private currentUserId: string | null = null;
  private currentUserProperties: UserProperties = {};

  /**
   * Initialize the analytics service
   * Waits for gtag to load with retry mechanism
   */
  initialize() {
    if (globalThis.window == undefined) return;

    const retryInterval = 500; // ms
    const maxRetries = 40;
    let retryCount = 0;

    // Wait for gtag to load with retry mechanism
    const checkGtag = setInterval(() => {
      retryCount++;

      if (globalThis.gtag) {
        this.isInitialized = true;
        this.flushQueue();
        clearInterval(checkGtag);

        if (analyticsConfig.debug) {
          console.log(
            `[Analytics] Initialized successfully - ${analyticsConfig.app.name} v${analyticsConfig.app.version}`
          );
        }
        return;
      }

      if (retryCount >= maxRetries) {
        clearInterval(checkGtag);
        console.warn(
          '[Analytics] gtag failed to load after',
          maxRetries,
          'attempts'
        );
      }
    }, retryInterval);
  }

  private flushQueue() {
    while (this.queue.length > 0) {
      const fn = this.queue.shift();
      fn?.();
    }
  }

  /**
   * Set user ID for tracking
   * @see https://developers.google.com/analytics/devguides/collection/ga4/user-id
   * @param userId - User ID to track, or null to clear on sign out
   */
  setUserId(userId: string | null) {
    const setUser = () => {
      try {
        this.currentUserId = userId;
        globalThis.gtag?.('config', analyticsConfig.measurementId, {
          user_id: userId,
        });

        if (analyticsConfig.debug) {
          console.log('[Analytics] User ID set:', userId);
        }
      } catch (error) {
        console.error('[Analytics] Error setting user ID:', error);
      }
    };

    if (this.isInitialized) {
      setUser();
    } else {
      this.queue.push(setUser);
    }
  }

  /**
   * Get current user ID
   */
  getUserId(): string | null {
    return this.currentUserId;
  }

  /**
   * Clear user ID on logout
   * @see https://developers.google.com/analytics/devguides/collection/ga4/user-id
   */
  clearUserId() {
    this.setUserId(null);
  }

  /**
   * Set user properties
   * @see https://developers.google.com/analytics/devguides/collection/ga4/user-properties
   * @param properties - User properties object
   */
  setUserProperties(properties: UserProperties) {
    const setProps = () => {
      try {
        // Store properties locally for use in events
        this.currentUserProperties = {
          ...this.currentUserProperties,
          ...properties,
        };

        globalThis.gtag?.('set', 'user_properties', properties);

        if (analyticsConfig.debug) {
          console.log('[Analytics] User properties set:', properties);
        }
      } catch (error) {
        console.error('[Analytics] Error setting user properties:', error);
      }
    };

    if (this.isInitialized) {
      setProps();
    } else {
      this.queue.push(setProps);
    }
  }

  /**
   * Get current user properties
   */
  getUserProperties(): UserProperties {
    return { ...this.currentUserProperties };
  }

  /**
   * Track an exception/error
   * @see https://developers.google.com/analytics/devguides/collection/ga4/exceptions
   * @param params - Exception parameters
   */
  trackException(params: ExceptionParams) {
    const track = () => {
      try {
        globalThis.gtag?.('event', 'exception', {
          ...params,
          ...analyticsConfig.defaultParams,
          send_to: analyticsConfig.measurementId,
        });

        if (analyticsConfig.debug) {
          console.log('[Analytics] Exception tracked:', params);
        }
      } catch (error) {
        console.error('[Analytics] Error tracking exception:', error);
      }
    };

    if (this.isInitialized) {
      track();
    } else {
      this.queue.push(track);
    }
  }

  /**
   * Track a custom event
   * @see https://developers.google.com/analytics/devguides/collection/ga4/event-parameters
   * @param name - Event name
   * @param version - Event version
   * @param attributes - Event parameters
   */
  trackEvent<T extends keyof TrackEvent>(
    name: T,
    version: keyof TrackEvent[T],
    attributes: TrackEvent[T][typeof version]
  ) {
    const track = () => {
      try {
        // Merge user properties with event data
        const eventData =
          isPlainObject(attributes) && attributes != null
            ? {
                ...(attributes as Record<string, unknown>),
                ...this.currentUserProperties, // Include user context
                ...analyticsConfig.defaultParams,
                send_to: analyticsConfig.measurementId,
              }
            : {
                ...this.currentUserProperties, // Include user context
                ...analyticsConfig.defaultParams,
                send_to: analyticsConfig.measurementId,
              };

        globalThis.gtag?.('event', name as string, eventData);

        if (analyticsConfig.debug) {
          console.log('[Analytics] Event tracked:', name, eventData);
        }
      } catch (error) {
        console.error('[Analytics] Error tracking event:', error);
      }
    };

    if (this.isInitialized) {
      track();
    } else {
      this.queue.push(track);
    }
  }

  /**
   * Track page views manually
   * Useful for SPAs
   * @param pagePath - Page path
   * @param pageTitle - Optional page title
   * @param additionalParams - Optional additional parameters
   */
  trackPageView(
    pagePath: string,
    pageTitle?: string,
    additionalParams?: Record<string, unknown>
  ) {
    const track = () => {
      try {
        globalThis.gtag?.('event', 'page_view', {
          ...additionalParams,
          ...analyticsConfig.defaultParams,
          send_to: analyticsConfig.measurementId,
          page_path: pagePath,
          page_title: pageTitle,
        });

        if (analyticsConfig.debug) {
          console.log('[Analytics] Page view tracked:', {
            pagePath,
            pageTitle,
            additionalParams,
          });
        }
      } catch (error) {
        console.error('[Analytics] Error tracking page view:', error);
      }
    };

    if (this.isInitialized) {
      track();
    } else {
      this.queue.push(track);
    }
  }
}

export default AnalyticsService;
